import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Tabs,
  Tab,
  Alert,
  alpha,
  useTheme,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  Print as PrintIcon,
  CloudDownload as CloudDownloadIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  DateRange as DateRangeIcon,
  Refresh as RefreshIcon,
  TableView as ExcelIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import {
  getTrialBalance,
  getIncomeStatement,
  getBalanceSheet,
  TrialBalanceResponse,
  IncomeStatementResponse,
  BalanceSheetResponse
} from '../../../services/financialReportsService';
import dayjs from 'dayjs';

// Interface for tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const FinancialReports: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // البيانات
  const [trialBalanceData, setTrialBalanceData] = useState<TrialBalanceResponse | null>(null);
  const [incomeStatementData, setIncomeStatementData] = useState<IncomeStatementResponse | null>(null);
  const [balanceSheetData, setBalanceSheetData] = useState<BalanceSheetResponse | null>(null);
  
  // التواريخ الافتراضية
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD')
  });

  // تحميل البيانات عند تغيير التاب أو التواريخ
  useEffect(() => {
    loadReportData();
  }, [tabValue, dateRange]);

  // تحميل بيانات التقرير المناسب
  const loadReportData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (tabValue === 0) {
        // ميزان المراجعة
        const data = await getTrialBalance(dateRange.startDate, dateRange.endDate);
        setTrialBalanceData(data);
      } else if (tabValue === 1) {
        // قائمة الدخل
        const data = await getIncomeStatement(dateRange.startDate, dateRange.endDate);
        setIncomeStatementData(data);
      } else if (tabValue === 2) {
        // قائمة المركز المالي
        const data = await getBalanceSheet(dateRange.endDate);
        setBalanceSheetData(data);
      }
    } catch (err: any) {
      console.error('خطأ في تحميل التقرير:', err);
      setError(err.response?.data?.message || 'حدث خطأ في تحميل التقرير');
    } finally {
      setLoading(false);
    }
  };

  // مناولة تغيير التاب
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // تحديث نطاق التاريخ
  const handleDateRangeChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange({
      ...dateRange,
      [field]: value
    });
  };

  // تصدير إلى Excel
  const exportToExcel = () => {
    let data: any[] = [];
    let fileName = '';
    
    if (tabValue === 0 && trialBalanceData) {
      // ميزان المراجعة
      fileName = `ميزان_المراجعة_${dayjs(dateRange.startDate).format('YYYY-MM-DD')}_${dayjs(dateRange.endDate).format('YYYY-MM-DD')}.xlsx`;
      data = trialBalanceData.data.map(item => ({
        'رقم الحساب': item.accountNumber,
        'اسم الحساب': item.accountName,
        'التصنيف': item.accountCategory || '-',
        'مدين': item.debit,
        'دائن': item.credit,
        'الرصيد': item.balance
      }));
      // إضافة صف المجموع
      data.push({
        'رقم الحساب': '',
        'اسم الحساب': 'المجموع',
        'التصنيف': '',
        'مدين': trialBalanceData.totals.totalDebit,
        'دائن': trialBalanceData.totals.totalCredit,
        'الرصيد': trialBalanceData.totals.difference
      });
    } else if (tabValue === 1 && incomeStatementData) {
      // قائمة الدخل
      fileName = `قائمة_الدخل_${dayjs(dateRange.startDate).format('YYYY-MM-DD')}_${dayjs(dateRange.endDate).format('YYYY-MM-DD')}.xlsx`;
      
      // الإيرادات
      data.push({ 'البند': 'الإيرادات', 'رقم الحساب': '', 'اسم الحساب': '', 'المبلغ': '' });
      incomeStatementData.data.revenues.forEach(item => {
        data.push({
          'البند': '',
          'رقم الحساب': item.accountNumber,
          'اسم الحساب': item.accountName,
          'المبلغ': item.amount
        });
      });
      data.push({
        'البند': '',
        'رقم الحساب': '',
        'اسم الحساب': 'إجمالي الإيرادات',
        'المبلغ': incomeStatementData.totals.totalRevenue
      });
      
      // المصروفات
      data.push({ 'البند': '', 'رقم الحساب': '', 'اسم الحساب': '', 'المبلغ': '' });
      data.push({ 'البند': 'المصروفات', 'رقم الحساب': '', 'اسم الحساب': '', 'المبلغ': '' });
      incomeStatementData.data.expenses.forEach(item => {
        data.push({
          'البند': '',
          'رقم الحساب': item.accountNumber,
          'اسم الحساب': item.accountName,
          'المبلغ': item.amount
        });
      });
      data.push({
        'البند': '',
        'رقم الحساب': '',
        'اسم الحساب': 'إجمالي المصروفات',
        'المبلغ': incomeStatementData.totals.totalExpenses
      });
      
      // صافي الدخل
      data.push({ 'البند': '', 'رقم الحساب': '', 'اسم الحساب': '', 'المبلغ': '' });
      data.push({
        'البند': '',
        'رقم الحساب': '',
        'اسم الحساب': 'صافي الدخل',
        'المبلغ': incomeStatementData.totals.netIncome
      });
    } else if (tabValue === 2 && balanceSheetData) {
      // قائمة المركز المالي
      fileName = `قائمة_المركز_المالي_${dayjs(dateRange.endDate).format('YYYY-MM-DD')}.xlsx`;
      
      // الأصول
      data.push({ 'البند': 'الأصول', 'رقم الحساب': '', 'اسم الحساب': '', 'المبلغ': '' });
      balanceSheetData.data.assets.forEach(item => {
        data.push({
          'البند': '',
          'رقم الحساب': item.accountNumber,
          'اسم الحساب': item.accountName,
          'المبلغ': item.amount
        });
      });
      data.push({
        'البند': '',
        'رقم الحساب': '',
        'اسم الحساب': 'إجمالي الأصول',
        'المبلغ': balanceSheetData.totals.totalAssets
      });
      
      // الخصوم
      data.push({ 'البند': '', 'رقم الحساب': '', 'اسم الحساب': '', 'المبلغ': '' });
      data.push({ 'البند': 'الخصوم', 'رقم الحساب': '', 'اسم الحساب': '', 'المبلغ': '' });
      balanceSheetData.data.liabilities.forEach(item => {
        data.push({
          'البند': '',
          'رقم الحساب': item.accountNumber,
          'اسم الحساب': item.accountName,
          'المبلغ': item.amount
        });
      });
      data.push({
        'البند': '',
        'رقم الحساب': '',
        'اسم الحساب': 'إجمالي الخصوم',
        'المبلغ': balanceSheetData.totals.totalLiabilities
      });
      
      // حقوق الملكية
      data.push({ 'البند': '', 'رقم الحساب': '', 'اسم الحساب': '', 'المبلغ': '' });
      data.push({ 'البند': 'حقوق الملكية', 'رقم الحساب': '', 'اسم الحساب': '', 'المبلغ': '' });
      balanceSheetData.data.equity.forEach(item => {
        data.push({
          'البند': '',
          'رقم الحساب': item.accountNumber,
          'اسم الحساب': item.accountName,
          'المبلغ': item.amount
        });
      });
      data.push({
        'البند': '',
        'رقم الحساب': '',
        'اسم الحساب': 'إجمالي حقوق الملكية',
        'المبلغ': balanceSheetData.totals.totalEquity
      });
      
      // المجموع
      data.push({ 'البند': '', 'رقم الحساب': '', 'اسم الحساب': '', 'المبلغ': '' });
      data.push({
        'البند': '',
        'رقم الحساب': '',
        'اسم الحساب': 'إجمالي الخصوم وحقوق الملكية',
        'المبلغ': balanceSheetData.totals.totalLiabilitiesAndEquity
      });
    }
    
    if (data.length > 0) {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'التقرير');
      XLSX.writeFile(wb, fileName);
    }
  };

  // تصدير إلى PDF
  const exportToPDF = () => {
    window.print();
  };

  // عرض ميزان المراجعة
  const renderTrialBalance = () => {
    if (!trialBalanceData) return null;

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
          ميزان المراجعة
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          الفترة من {dayjs(dateRange.startDate).format('DD/MM/YYYY')} إلى {dayjs(dateRange.endDate).format('DD/MM/YYYY')}
        </Typography>
        
        {trialBalanceData.data.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            لا توجد بيانات لعرضها في هذه الفترة
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                  <TableCell>رقم الحساب</TableCell>
                  <TableCell>اسم الحساب</TableCell>
                  <TableCell>التصنيف</TableCell>
                  <TableCell align="right">مدين</TableCell>
                  <TableCell align="right">دائن</TableCell>
                  <TableCell align="right">الرصيد</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trialBalanceData.data.map((account) => (
                  <TableRow key={account.accountId} hover>
                    <TableCell>{account.accountNumber}</TableCell>
                    <TableCell>
                      {account.accountName}
                      {account.parentAccountName && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          تابع لـ: {account.parentAccountName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main
                      }}>
                        {account.accountCategory || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: account.debit > 0 ? 'success.main' : undefined }}>
                      {account.debit > 0 ? `${account.debit.toLocaleString()} ر.س` : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: account.credit > 0 ? 'error.main' : undefined }}>
                      {account.credit > 0 ? `${account.credit.toLocaleString()} ر.س` : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 500,
                      color: account.balance > 0 ? 'success.main' : account.balance < 0 ? 'error.main' : undefined
                    }}>
                      {account.balance.toLocaleString()} ر.س
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), '& td': { fontWeight: 600 } }}>
                  <TableCell colSpan={3}>المجموع</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>
                    {trialBalanceData.totals.totalDebit.toLocaleString()} ر.س
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>
                    {trialBalanceData.totals.totalCredit.toLocaleString()} ر.س
                  </TableCell>
                  <TableCell align="right">
                    {trialBalanceData.totals.difference.toLocaleString()} ر.س
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {trialBalanceData.totals.difference !== 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            تحذير: هناك فرق في الميزان بمقدار {Math.abs(trialBalanceData.totals.difference).toLocaleString()} ر.س
          </Alert>
        )}
      </Box>
    );
  };

  // عرض قائمة الدخل
  const renderIncomeStatement = () => {
    if (!incomeStatementData) return null;

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
          قائمة الدخل
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          الفترة من {dayjs(dateRange.startDate).format('DD/MM/YYYY')} إلى {dayjs(dateRange.endDate).format('DD/MM/YYYY')}
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            الإيرادات
          </Typography>
          
          {incomeStatementData.data.revenues.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              لا توجد إيرادات في هذه الفترة
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  {incomeStatementData.data.revenues.map((item) => (
                    <TableRow key={item.accountId} hover>
                      <TableCell sx={{ width: '20%' }}>{item.accountNumber}</TableCell>
                      <TableCell>{item.accountName}</TableCell>
                      <TableCell align="right" sx={{ width: '25%', color: 'success.main' }}>
                        {item.amount.toLocaleString()} ر.س
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), '& td': { fontWeight: 600 } }}>
                    <TableCell colSpan={2}>إجمالي الإيرادات</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      {incomeStatementData.totals.totalRevenue.toLocaleString()} ر.س
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            المصروفات
          </Typography>
          
          {incomeStatementData.data.expenses.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              لا توجد مصروفات في هذه الفترة
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  {incomeStatementData.data.expenses.map((item) => (
                    <TableRow key={item.accountId} hover>
                      <TableCell sx={{ width: '20%' }}>{item.accountNumber}</TableCell>
                      <TableCell>{item.accountName}</TableCell>
                      <TableCell align="right" sx={{ width: '25%', color: 'error.main' }}>
                        {item.amount.toLocaleString()} ر.س
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), '& td': { fontWeight: 600 } }}>
                    <TableCell colSpan={2}>إجمالي المصروفات</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {incomeStatementData.totals.totalExpenses.toLocaleString()} ر.س
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderColor: alpha(theme.palette.primary.main, 0.2)
            }}
          >
            <Grid container alignItems="center">
              <Grid item xs={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  صافي الدخل
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography 
                  variant="h6" 
                  align="right" 
                  sx={{ 
                    fontWeight: 600, 
                    color: incomeStatementData.totals.netIncome >= 0 ? 'success.main' : 'error.main' 
                  }}
                >
                  {incomeStatementData.totals.netIncome.toLocaleString()} ر.س
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
    );
  };

  // عرض قائمة المركز المالي
  const renderBalanceSheet = () => {
    if (!balanceSheetData) return null;

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
          قائمة المركز المالي
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          كما في {dayjs(dateRange.endDate).format('DD/MM/YYYY')}
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            الأصول
          </Typography>
          
          {balanceSheetData.data.assets.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              لا توجد أصول مسجلة
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  {balanceSheetData.data.assets.map((item) => (
                    <TableRow key={item.accountId} hover>
                      <TableCell sx={{ width: '20%' }}>{item.accountNumber}</TableCell>
                      <TableCell>{item.accountName}</TableCell>
                      <TableCell align="right" sx={{ width: '25%' }}>
                        {item.amount.toLocaleString()} ر.س
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), '& td': { fontWeight: 600 } }}>
                    <TableCell colSpan={2}>إجمالي الأصول</TableCell>
                    <TableCell align="right">
                      {balanceSheetData.totals.totalAssets.toLocaleString()} ر.س
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            الخصوم
          </Typography>
          
          {balanceSheetData.data.liabilities.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              لا توجد خصوم مسجلة
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  {balanceSheetData.data.liabilities.map((item) => (
                    <TableRow key={item.accountId} hover>
                      <TableCell sx={{ width: '20%' }}>{item.accountNumber}</TableCell>
                      <TableCell>{item.accountName}</TableCell>
                      <TableCell align="right" sx={{ width: '25%' }}>
                        {item.amount.toLocaleString()} ر.س
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), '& td': { fontWeight: 600 } }}>
                    <TableCell colSpan={2}>إجمالي الخصوم</TableCell>
                    <TableCell align="right">
                      {balanceSheetData.totals.totalLiabilities.toLocaleString()} ر.س
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            حقوق الملكية
          </Typography>
          
          {balanceSheetData.data.equity.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              لا توجد حقوق ملكية مسجلة
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  {balanceSheetData.data.equity.map((item) => (
                    <TableRow key={item.accountId} hover>
                      <TableCell sx={{ width: '20%' }}>{item.accountNumber}</TableCell>
                      <TableCell>{item.accountName}</TableCell>
                      <TableCell align="right" sx={{ width: '25%' }}>
                        {item.amount.toLocaleString()} ر.س
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), '& td': { fontWeight: 600 } }}>
                    <TableCell colSpan={2}>إجمالي حقوق الملكية</TableCell>
                    <TableCell align="right">
                      {balanceSheetData.totals.totalEquity.toLocaleString()} ر.س
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderColor: alpha(theme.palette.primary.main, 0.2)
            }}
          >
            <Grid container alignItems="center">
              <Grid item xs={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  إجمالي الخصوم وحقوق الملكية
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography 
                  variant="h6" 
                  align="right" 
                  sx={{ fontWeight: 600 }}
                >
                  {balanceSheetData.totals.totalLiabilitiesAndEquity.toLocaleString()} ر.س
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          
          {Math.abs(balanceSheetData.totals.totalAssets - balanceSheetData.totals.totalLiabilitiesAndEquity) > 0.01 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              تحذير: هناك عدم توازن في المركز المالي. الأصول لا تساوي الخصوم + حقوق الملكية
            </Alert>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
          background: 'white'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <BarChartIcon sx={{ mr: 1, color: '#30c3b0' }} />
            التقارير المالية
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="طباعة التقرير" arrow>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={exportToPDF}
                sx={{
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05)
                  }
                }}
              >
                طباعة
              </Button>
            </Tooltip>
            
            <Tooltip title="تصدير إلى PDF" arrow>
              <Button
                variant="outlined"
                startIcon={<CloudDownloadIcon />}
                onClick={exportToPDF}
                sx={{
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.error.main, 0.5),
                  color: theme.palette.error.main,
                  '&:hover': {
                    borderColor: theme.palette.error.main,
                    bgcolor: alpha(theme.palette.error.main, 0.05)
                  }
                }}
              >
                PDF
              </Button>
            </Tooltip>
            
            <Tooltip title="تصدير إلى Excel" arrow>
              <Button
                variant="outlined"
                startIcon={<ExcelIcon />}
                onClick={exportToExcel}
                sx={{
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.success.main, 0.5),
                  color: theme.palette.success.main,
                  '&:hover': {
                    borderColor: theme.palette.success.main,
                    bgcolor: alpha(theme.palette.success.main, 0.05)
                  }
                }}
              >
                Excel
              </Button>
            </Tooltip>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={9}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <DateRangeIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="subtitle1">
                نطاق التاريخ:
              </Typography>
              
              <TextField
                type="date"
                size="small"
                label="من"
                value={dateRange.startDate}
                onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                type="date"
                size="small"
                label="إلى"
                value={dateRange.endDate}
                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Tooltip title="تحديث البيانات" arrow>
              <IconButton
                onClick={loadReportData}
                disabled={loading}
                sx={{
                  bgcolor: alpha('#30c3b0', 0.1),
                  color: '#30c3b0',
                  '&:hover': {
                    bgcolor: alpha('#30c3b0', 0.2)
                  },
                  '&:disabled': {
                    bgcolor: alpha(theme.palette.action.disabled, 0.1)
                  }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            aria-label="financial reports tabs"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#30c3b0',
                height: 3,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: '#30c3b0',
                  fontWeight: 600,
                },
              },
            }}
          >
            <Tab 
              label="ميزان المراجعة" 
              icon={<AssessmentIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="قائمة الدخل" 
              icon={<TrendingUpIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="قائمة المركز المالي" 
              icon={<AccountBalanceWalletIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
          </Box>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              {renderTrialBalance()}
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              {renderIncomeStatement()}
            </TabPanel>
            
            <TabPanel value={tabValue} index={2}>
              {renderBalanceSheet()}
            </TabPanel>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default FinancialReports;